import MergeTypeIcon from '@mui/icons-material/MergeType';
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { GRID_CHECKBOX_SELECTION_COL_DEF, useGridApiRef } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import { postSplitOrder, getOrderLogData, postSaveOrderAddress } from '../../../../api/orders';
import { markMetricAddedIfCounted, NotificationMetricType } from '../../../../redux/slices/notifications';
import { useTabs } from '../../../../context/TabContext';
import {
  addItemsToAnOrderInRedux,
  cartCheckWithData,
  clearTempOrderList,
  getAddToOrderList,
  getPendingOrderInfo,
  getPendingOrders,
  updateOrderAddressInRedux,
} from '../../../../redux/slices/orders';
import { setBudgetPpdInfo } from '../../../../redux/slices/orderSheet';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { CollapseSidebar } from '../../../../reusable-components/collapse-sidebar';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { ADD_TO_ORDER, PENDING_ORDER } from '../../../../reusable-components/datagrid/pendingOrderColumns';
import Iconify from '../../../../reusable-components/iconify';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { BudgetBox } from '../new-order';
import AddItemQuantityField from './AddItemQuantityField';
import CartCheck from './CartCheck';
import ConfirmOrderDialog from './ConfirmOrderModal';
import CustomToolbar from './CustomToolbar';
import OrderInfo from './OrderInfo';
import OrderReviewers from './OrderReviewers';
import OrderTicket from './OrderTicket';
import VendorInfo from './VendorInfo';
import OrderLog from './orderLog/OrderLog';
import { parseLogItems } from './orderLog/orderLogHelper';
import { highlightGridRow } from '../../../../reusable-components/activity-log/logHelperFunctions';

export default function PendingOrderView() {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const {
    orderById,
    addToOrderListById,
    tempOrderListById = [],
    cartCheckItemsById,
    data: { pendingOrders },
  } = useSelector((state) => state.orders);

  const {
    items = [],
    facilityId,
    budgets = [],
    vendorDates,
    orderAddress,
    vendors,
    facilityAddress,
  } = orderById[id] || {};
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { addNewTabAndNavigate } = useTabs();
  const cartCheckAccess = useScopeCheck(['Edit-Order', 'Cart-Check'], true);

  const [data, setData] = useState([]);
  const [currentTicket, setCurrentTicket] = useState(0);
  const [allReqDates, setAllReqDates] = useState([]);
  const [openSidebar, setOpenSidebar] = useState(true);
  const [addItemData, setAddItemData] = useState([]);
  const [addItemCategoryId, setAddItemCategoryId] = useState(null);
  const [openProcessDialog, setOpenProcessDialog] = useState(false);
  const [firstCartCheck, setFirstCartCheck] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(undefined);
  const [isVendorInfoOpen, setIsVendorInfoOpen] = useState(true);
  const [isOrderLogOpen, setIsOrderLogOpen] = useState(false);
  const [isOrderLogDataLoading, setIsOrderLogDataLoading] = useState(false);
  const [isBudgetBoxOpen, setIsBudgetBoxOpen] = useState(true);
  const [isOrderInfoOpen, setIsOrderInfoOpen] = useState(false);
  const [isOrderInfoContainerOpen, setIsOrderInfoContainerOpen] = useState(true);
  const [orderLogData, setOrderLogData] = useState(null);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [orderLogUpdated, setOrderLogUpdated] = useState(false);
  const [isAddressEditedDialogOpen, setIsAddressEditedDialogOpen] = useState(false);

  const orderQueue = JSON.parse(localStorage.getItem('selectedOrders'));

  const pendingOrderApiRef = useGridApiRef();

  useEffect(() => {
    return () => localStorage.removeItem('selectedOrders');
  }, []);

  useEffect(() => () => dispatch(setBudgetPpdInfo()), [id]);

  useEffect(() => {
    if (isOrderLogOpen && (!orderLogData || orderLogUpdated) && !isOrderLogDataLoading) {
      fetchOrderLogData();
    }
  }, [isOrderLogOpen, orderLogUpdated]);

  useEffect(() => {
    setOrderLogData(null);
    setHighlightedRow(null);
    if (isOrderLogOpen) fetchOrderLogData();
  }, [id]);

  useEffect(() => {
    if (highlightedRow) {
      highlightGridRow(setHighlightedRow, highlightedRow, pendingOrderApiRef.current);
    }
  }, [highlightedRow]);

  const fetchOrderLogData = () => {
    if (isOrderLogOpen) {
      setIsOrderLogDataLoading(true);
      getOrderLogData(id)
        .then((data) => {
          setOrderLogData({ ...data, logItems: parseLogItems(data?.logItems) });
        })
        .catch(() => enqueueSnackbar(`Error fetching order log data`, { variant: 'error' }))
        .finally(() => {
          setIsOrderLogDataLoading(false);
          setOrderLogUpdated(false);
        });
    }
  };

  useEffect(() => {
    if (items) {
      const arrayOfIdsThatHaveErrors = cartCheckItemsById[id]
        ?.filter((item) => item?.errors?.length > 0)
        ?.map((item) => item.id);
      const filteredItems = applyTableFilters(items, selectedVendor?.id);
      setData(applyTableSorts(filteredItems, arrayOfIdsThatHaveErrors));
    }
  }, [items, cartCheckItemsById, selectedVendor, id]);

  const applyTableFilters = (items, selectedVendorId) => {
    const filteredItems = items?.filter((item) => !item.temporary) ?? [];
    return selectedVendorId ? filteredItems.filter((item) => item.vendorId === selectedVendorId) : filteredItems;
  };

  const applyTableSorts = (items, arrayOfIdsThatHaveErrors) => {
    if (!arrayOfIdsThatHaveErrors?.length) return items;
    return items.sort(
      (a, b) =>
        arrayOfIdsThatHaveErrors.includes(b.id) - arrayOfIdsThatHaveErrors.includes(a.id) ||
        a.vendor?.localeCompare(b.vendor)
    );
  };

  const handleCancel = () => {
    setAddItemCategoryId(null);
    dispatch(clearTempOrderList(id));
  };

  const goToNext = () => {
    setOpenProcessDialog(false);
    const ordersLeft = orderQueue?.length;
    const params = new URLSearchParams(window.location.search);
    const section = params.get('filter');
    const rerunFromVendorView = params.get('rerunFromVendorView') === '1';

    if (ordersLeft > -1) {
      if (ordersLeft === 0) {
        localStorage.removeItem('selectedOrders');
        navigate(`/dashboard/orders?filter=${section}`);
      } else {
        const nextId = orderQueue[0];
        localStorage.setItem('selectedOrders', JSON.stringify(orderQueue.slice(1)));
        navigate(`/dashboard/orders/${nextId}?filter=${section}`);
      }
    } else if (section === 'rerun') {
      const start = params.get('start') || new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();
      const end = params.get('end') || new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString();
      const backToOrdersParams = new URLSearchParams({
        filter: 'previous',
        start,
        end,
      });
      if (rerunFromVendorView) backToOrdersParams.set('viewByVendor', '1');
      navigate(`/dashboard/orders?${backToOrdersParams.toString()}`);
    } else navigate(`/dashboard/orders?filter=${section}`);
  };

  const handleCloseAddressEditedDialog = () => {
    setIsAddressEditedDialogOpen(false);
  };

  const handleClearItemAdditions = () => {
    dispatch(clearTempOrderList(id));
    handleCloseAddressEditedDialog();
  };
  const disallowedTempItems = useMemo(
    () =>
      tempOrderListById[id]?.filter((item) => {
        const vendor = vendors?.find((v) => v.id === item.vendorId);
        return !vendor?.allowsAddressEdits;
      }) ?? [],
    [tempOrderListById, id, vendors]
  );

  const disallowedIds = useMemo(() => new Set(disallowedTempItems.map((item) => item.id)), [disallowedTempItems]);

  const allowedItems = useMemo(
    () => tempOrderListById[id]?.filter((item) => !disallowedIds.has(item.id)) || [],
    [disallowedIds]
  );

  const handleAddAllowedItemsOnly = () => {
    addItemsToOrder(allowedItems);
    setIsAddressEditedDialogOpen(false);
  };

  const setOrderAddressToFacilityAddress = () =>
    postSaveOrderAddress(id, facilityAddress)
      .then(() => {
        dispatch(updateOrderAddressInRedux(id, null));
        addItemsToOrder();
        setIsAddressEditedDialogOpen(false);
        enqueueSnackbar('Shipping address reset to facility address', { variant: 'success' });
      })
      .catch(() => enqueueSnackbar('Failed to reset shipping address', { variant: 'error' }));

  const handleAddItemsToOrderClick = () => {
    const isAddressEdited = !!orderAddress && !isEqual(orderAddress, facilityAddress);
    const newVendorsAllowAddressEdits = tempOrderListById[id]?.every((item) => {
      const vendor = vendors?.find((vendor) => vendor.id === item.vendorId);
      return vendor?.allowsAddressEdits;
    });
    if (!isAddressEdited || newVendorsAllowAddressEdits) addItemsToOrder();
    else setIsAddressEditedDialogOpen(true);
  };

  const addItemsToOrder = (items = tempOrderListById[id]) => {
    dispatch(
      addItemsToAnOrderInRedux({
        orderId: id,
        arrayOfItems: items?.map((item) => ({
          ...item,
          approved: true,
          temporary: false,
          unsavedId: 0,
        })),
      })
    );
    setAddItemCategoryId(null);
    dispatch(clearTempOrderList(id));
  };

  useEffect(() => {
    setAllReqDates(
      vendorDates?.map((vendorDate) => ({ vendorId: vendorDate.vendorId, date: new Date(vendorDate.requestedDate) })) ??
        []
    );
  }, [vendorDates]);

  useEffect(() => {
    if (addItemCategoryId) {
      setAddItemData([]);
      dispatch(getAddToOrderList(id, facilityId, addItemCategoryId));
    }
  }, [addItemCategoryId, facilityId, id]);

  useEffect(() => {
    setAddItemData(addToOrderListById[id] || []);
  }, [addToOrderListById, id]);

  const [selected, setSelected] = useState([]);
  const [splitOrder, setSplitOrder] = useState(false);
  const [openTicket, setOpenTicket] = useState('close');
  const [activeRows, setActiveRows] = useState(undefined);
  const [addItemsToTicket, setAddItemsToTicket] = useState(false);

  useEffect(() => {
    setOpenTicket('close');
  }, [id]);

  useEffect(() => {
    if (openTicket === 'open') {
      setIsOrderInfoOpen(false);
      setIsVendorInfoOpen(false);
      setIsBudgetBoxOpen(false);
      setIsOrderInfoContainerOpen(false);
      setIsOrderLogOpen(false);
    } else {
      setIsVendorInfoOpen(true);
      setIsBudgetBoxOpen(true);
      setIsOrderInfoContainerOpen(true);
    }
  }, [openTicket]);

  const departments = useMemo(() => {
    const seen = new Set();
    const result = [];
    const allItems = [...(items || []), ...(tempOrderListById[id] || [])];
    allItems.forEach((row) => {
      if (row.agoraCategory && row.agoraCategory.name && !seen.has(row.agoraCategory.name)) {
        seen.add(row.agoraCategory.name);
        result.push({ name: row.agoraCategory.name, id: row.agoraCategory.id });
      }
    });

    return result;
  }, [items, id]);

  const selectedRows = useMemo(() => data?.filter((row) => selected.includes(row.id)), [data, selected]);

  const isLoading = useSelector((state) => state.orders.isLoading);
  const itemsIsLoading = useSelector((state) => state.orderSheet.isLoading);
  const [updatedCartItems, setUpdatedCartItems] = useState([]);

  const [cartCheckIsLoading, setCartCheckIsLoading] = useState(false);
  const hasError = (items) => items?.some((item) => item.errors?.length > 0);

  const cartCheckFunction = async (errorDialogShouldOpen = true) => {
    if (!cartCheckAccess || !facilityId || !data || data.length === 0) return;
    setCartCheckIsLoading(true);
    const cartCheck = await dispatch(cartCheckWithData(id, facilityId, data, errorDialogShouldOpen));

    const { items, vendorErrors } = cartCheck;

    if (hasError(items)) {
      enqueueSnackbar('There are errors in the order', { variant: 'error' });
    } else {
      enqueueSnackbar('Order checked successfully', { variant: 'success' });
    }

    if (vendorErrors?.length > 0) {
      vendorErrors.forEach((error) => {
        enqueueSnackbar(error, { variant: 'error', autoHideDuration: null });
      });
    }

    setCartCheckIsLoading(false);
  };

  useEffect(() => {
    if (firstCartCheck && data?.length > 0) {
      setFirstCartCheck(false);
      cartCheckFunction(false);
    }
  }, [firstCartCheck, data]);

  const CustomLeftToolbar = useCallback(
    () =>
      !addItemsToTicket ? (
        <CustomToolbar
          setSelected={setSelected}
          setSplitOrder={setSplitOrder}
          addItemCategoryId={addItemCategoryId}
          setAddItemCategoryId={setAddItemCategoryId}
          setOpenProcessDialog={setOpenProcessDialog}
          allReqDates={allReqDates}
          activeRows={activeRows}
          goToNextOrder={goToNext}
          handleVendorFilterClick={(vendor) => setSelectedVendor(vendor)}
          selectedVendor={selectedVendor}
          updatedCartItems={updatedCartItems}
          cartCheckFunction={cartCheckFunction}
          isLoading={cartCheckIsLoading}
          fetchOrderLogData={fetchOrderLogData}
          setOrderLogUpdated={setOrderLogUpdated}
        />
      ) : (
        <Box sx={{ display: 'flex', mx: 1 }}>
          <Typography variant="subtitle1">Select items to add to ticket</Typography>
          <Typography
            variant="subtitle1"
            color="secondary"
            sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1 }}
            onClick={() => setAddItemsToTicket(false)}
          >
            Cancel
          </Typography>
        </Box>
      ),
    [addItemCategoryId, data, allReqDates, activeRows, addItemsToTicket, cartCheckIsLoading]
  );
  const fetchData = async () => {
    await dispatch(getPendingOrders());
  };

  useEffect(() => {
    if (pendingOrders === undefined || pendingOrders?.length === 0) {
      fetchData();
    }
  }, []);

  const splitOrderFunction = async () => {
    const response = await postSplitOrder({ orderId: id, selectedRows, data });
    if (response.status === 200) {
      dispatch(markMetricAddedIfCounted(
        NotificationMetricType.OrdersSubmitted,
        id,
        response.data[1]?.id
      ));
      dispatch(getPendingOrderInfo(id));
      setSelected([]);
      setSplitOrder(false);
      enqueueSnackbar(`Order #${id} split ${selectedRows?.length} items to `, {
        variant: 'success',
        autoHideDuration: 4000,
        action: (key) => (
          <>
            <Button
              onClick={() => {
                addNewTabAndNavigate(`/dashboard/orders/${response.data[1]?.id}`);
              }}
              color="secondary"
              endIcon={<Iconify icon="material-symbols:open-in-new" />}
            >
              #{response.data[1]?.id}
            </Button>

            <Button
              onClick={() => {
                closeSnackbar(key);
              }}
              sx={{
                color: '#fff',
              }}
            >
              X
            </Button>
          </>
        ),
      });
    } else {
      enqueueSnackbar('Error splitting order', { variant: 'error', persist: true });
    }
  };

  const quantity = {
    field: 'quantity',
    headerName: 'Qty',
    width: 100,
    renderCell: (params) => (
      <AddItemQuantityField
        row={params.row}
        isIdFound={data?.some((item) => item.itemId === params.row.id && item.deleted === false)}
        isPunchoutOrderLocked={data?.some(
          (item) => item.itemId === -1 && item.punchoutOrderLocked && item.vendorId === params.row.vendorId
        )}
      />
    ),
  };

  return (
    <>
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
        <Grid item xs={openSidebar ? 9.3 : 11.8}>
          <Grid container>
            {splitOrder && (
              <Grid item xs={2.5}>
                <Box
                  sx={{
                    ml: 2,
                    p: 1.5,
                    mt: 4,
                  }}
                >
                  <AwaitButton
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ mb: 1, width: '100%', boxShadow: 3, fontWeight: 'bold' }}
                    startIcon={<MergeTypeIcon />}
                    onClick={splitOrderFunction}
                  >
                    Split from {id}
                  </AwaitButton>
                  <Box>
                    {selectedRows.map((row, index) => (
                      <Box key={index}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: 'solid 2px #b128bc',
                            width: '100%',
                            height: 'auto',
                            mr: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 'auto',
                              backgroundColor: 'secondary.main',
                              display: 'flex',
                              height: '100%',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              pr: '2px',
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box sx={{ ml: 1, color: 'secondary.main', fontSize: '0.8rem' }}>{row.description}</Box>
                        </Box>
                        {index < selectedRows?.length - 1 && (
                          <Box
                            sx={{
                              width: '2px',
                              height: '20px',
                              backgroundColor: '#b128bc',
                              mx: 'auto',
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                    <Grid item xs={6}>
                      <Button
                        variant="text"
                        color="inherit"
                        sx={{
                          mb: 1,
                          textDecoration: 'underline',
                        }}
                        onClick={() => {
                          setSelected([]);
                          setSplitOrder(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
            <Grid item xs={splitOrder > 0 ? 9.5 : 12}>
              <ScopeGuard scopes={['Cart-Check', 'Edit-Order']} allowAdmin>
                <Grid item>
                  <CartCheck
                    updatedCartItems={updatedCartItems}
                    setUpdatedCartItems={setUpdatedCartItems}
                    setData={setData}
                  />
                </Grid>
              </ScopeGuard>
              <Box>
                {addItemCategoryId && (
                  <>
                    <CustomDataGrid
                      gridId="orders-pending-add-to-order"
                      isModal
                      scrollbarHeight={58}
                      boxSX={{ height: '34vh' }}
                      data={addItemData}
                      gridColumns={[...ADD_TO_ORDER, quantity]}
                      getRowClassName={(params) => {
                        const isIdFound = data?.some(
                          (item) =>
                            (item.itemId === params.row.id && !item.deleted) ||
                            (item.itemId === -1 && item.punchoutOrderLocked && item.vendorId === params.row.vendorId)
                        );

                        if (isIdFound) {
                          return 'disabled';
                        }
                        return '';
                      }}
                      dontGetRowHeight
                      pinnedColumns={{
                        left: ['picture'],
                        right: ['vendor', 'total', 'quantity'],
                      }}
                      isLoading={itemsIsLoading || addItemData?.length === 0}
                      CustomLeftToolbar={CustomLeftToolbar}
                    />
                    <Grid container direction="row" justifyContent="flex-end" alignItems="flex-end" sx={{ my: 1 }}>
                      <Grid item>
                        <Button sx={{ mr: 1 }} size="small" variant="outlined" color="error" onClick={handleCancel}>
                          Cancel
                        </Button>
                      </Grid>
                      <Grid item>
                        <AwaitButton
                          color="primary"
                          size="small"
                          variant="contained"
                          sx={{ mr: 1 }}
                          onClick={handleAddItemsToOrderClick}
                          disabled={tempOrderListById[id]?.length === 0}
                        >
                          Add to order
                        </AwaitButton>
                      </Grid>
                    </Grid>
                  </>
                )}

                <CustomDataGrid
                  apiRef={pendingOrderApiRef}
                  gridId="orders-pending-order-view"
                  scrollbarHeight={addItemCategoryId ? 58 : 100}
                  boxSX={{ height: addItemCategoryId ? '38vh' : 'calc(100vh - 270px)' }}
                  data={data}
                  gridColumns={[...PENDING_ORDER]}
                  getRowClassName={(params) => {
                    const classes = [];
                    if (params.row.deleted === true) classes.push('disabled');
                    if (highlightedRow === params.row.id) classes.push('row--highlight');
                    if (params.row.deactivated) classes.push('disabled', 'allow-click');
                    return classes.join(' ');
                  }}
                  dontGetRowHeight
                  rowSelectionModel={selected}
                  onRowSelectionModelChange={(newSelection) => {
                    setSelected(newSelection);
                  }}
                  isRowSelectable={({ row }) => row.deleted !== true}
                  pinnedColumns={{
                    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'picture'],
                    right: ['vendor', 'total', 'quantity', 'removedFromBudget', 'approved'],
                  }}
                  checkboxSelection={splitOrder || !!addItemsToTicket}
                  isLoading={data?.length === 0 || isLoading}
                  hideToolbar={!!addItemCategoryId}
                  CustomLeftToolbar={CustomLeftToolbar}
                  setActiveRows={(rows) => setActiveRows(rows)}
                  sx={{
                    '& .MuiDataGrid-row.row--highlight': {
                      backgroundColor: (theme) => {
                        if (theme.palette.mode === 'light') {
                          return 'rgba(143, 223, 130, 0.3)';
                        }
                        return 'rgba(143, 223, 130, 1)';
                      },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={openSidebar ? 2.7 : 0.2}>
          <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" height="100%">
            <CollapseSidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar}>
              <Box
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', paddingRight: '3px' }}
                className="hover-scrollbar"
              >
                {openSidebar ? (
                  <Box>
                    {!isOrderInfoContainerOpen ? (
                      <Card sx={{ p: 1, width: '100%' }}>
                        <Grid
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-end"
                          onClick={() => setIsOrderInfoContainerOpen(!isOrderInfoContainerOpen)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
                            Order Information
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', cursor: 'pointer' }} color="info.main">
                            <Iconify
                              icon={isOrderInfoContainerOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                              width={16}
                              height={16}
                            />
                          </Typography>
                        </Grid>
                      </Card>
                    ) : (
                      <>
                        <OrderReviewers pending />
                        <OrderInfo open={isOrderInfoOpen} setOpen={setIsOrderInfoOpen} />
                        <OrderLog
                          open={isOrderLogOpen}
                          setOpen={setIsOrderLogOpen}
                          orderLogData={orderLogData}
                          setHighlightedRow={setHighlightedRow}
                          isLoading={isOrderLogDataLoading}
                          orderType={
                            data?.every((item) => item.itemId === -1)
                              ? 'Punchout'
                              : data?.some((item) => item.itemId === -1)
                                ? 'Hosted/punchout'
                                : 'Hosted'
                          }
                        />
                        <VendorInfo
                          allReqDates={allReqDates}
                          setAllReqDates={setAllReqDates}
                          open={isVendorInfoOpen}
                          setOpen={setIsVendorInfoOpen}
                        />
                        <ScopeGuard scopes={['View-Order']} allowAdmin>
                          {departments?.map((department) => (
                            <Grid item key={department.id} sx={{ mt: 2, width: '100%' }}>
                              <BudgetBox
                                budget={budgets?.find((budget) => budget.category === department.name)}
                                cart={[...(items || []), ...(tempOrderListById[id] || [])].filter(
                                  (item) =>
                                    item.quantity > 0 &&
                                    item.approved === true &&
                                    item.deleted !== true &&
                                    item.agoraCategory?.name === department.name
                                )}
                                catId={department?.id || null}
                                facId={facilityId || null}
                                open={isBudgetBoxOpen}
                                setOpen={setIsBudgetBoxOpen}
                              />
                            </Grid>
                          ))}
                        </ScopeGuard>
                      </>
                    )}
                  </Box>
                ) : (
                  <></>
                )}
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    minHeight: openTicket === 'close' ? 0 : '50vh',
                    position: !openSidebar ? 'fixed' : openTicket === 'close' ? 'sticky' : 'inherit',
                    right: !openSidebar ? '20px' : 'inherit',
                    bottom: !openSidebar ? '35px' : 0,
                    mt: 2,
                  }}
                >
                  <OrderTicket
                    currentTicket={currentTicket}
                    setCurrentTicket={setCurrentTicket}
                    openTicket={openTicket}
                    setOpenTicket={setOpenTicket}
                    addItemsToTicket={addItemsToTicket}
                    setAddItemsToTicket={setAddItemsToTicket}
                    selectedRows={splitOrder ? [] : selectedRows}
                    setSelected={setSelected}
                    openSidebar={openSidebar}
                    setOpenSidebar={setOpenSidebar}
                    isOrderInfoContainerOpen={isOrderInfoContainerOpen}
                    setIsOrderInfoContainerOpen={setIsOrderInfoContainerOpen}
                    orderItems={data}
                    setHighlightedRow={setHighlightedRow}
                  />
                </Box>
              </Box>
            </CollapseSidebar>
          </Grid>
        </Grid>
        <ConfirmOrderDialog
          openConfirm={openProcessDialog}
          setOpenConfirm={setOpenProcessDialog}
          id={id}
          allReqDates={allReqDates}
          goToNextOrder={goToNext}
        />
        <Dialog
          fullWidth
          open={isAddressEditedDialogOpen}
          onClose={(_, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            handleCloseAddressEditedDialog();
          }}
        >
          <DialogTitle sx={{ textAlign: 'center' }}>
            Some items cannot ship to the edited address
            <IconButton
              onClick={handleCloseAddressEditedDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Iconify icon="ic:sharp-close" width={28} height={28} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ typography: 'body2' }}>
            <Typography sx={{ fontWeight: 'bold' }}> Must ship to the facility address: </Typography>
            <ul style={{ marginLeft: '16px' }}>
              {disallowedTempItems.map((item) => (
                <li key={item.id}>
                  {item.description} – {vendors?.find((vendor) => vendor.id === item.vendorId)?.name}
                </li>
              ))}
            </ul>
            {allowedItems.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 'bold', mt: 2 }}> Can ship to the edited address: </Typography>
                <ul style={{ marginLeft: '16px' }}>
                  {allowedItems.map((item) => (
                    <li key={item.id}>
                      {item.description} – {vendors?.find((vendor) => vendor.id === item.vendorId)?.name}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2 }}>
            <AwaitButton variant="contained" color="primary" onClick={setOrderAddressToFacilityAddress}>
              Use Facility Address
            </AwaitButton>
            {disallowedTempItems.length < tempOrderListById[id]?.length ? (
              <AwaitButton
                variant="outlined"
                color="primary"
                onClick={handleAddAllowedItemsOnly}
                disabled={disallowedTempItems.length === tempOrderListById[id]?.length}
              >
                Add Eligible Items Only
              </AwaitButton>
            ) : (
              <Button variant="outlined" color="primary" onClick={handleClearItemAdditions}>
                Remove All Items
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Grid>
    </>
  );
}
